import React, { useEffect, useMemo, useState } from 'react';
import { Moon, Sun, Users as UsersIcon } from 'lucide-react';
import type { AppModule, MyGroup, User } from '../../types';
import { userService } from '../../services/userService';
import { moduleService } from '../../services/moduleService';
import { groupService } from '../../services/groupService';
import { roleService } from '../../services/roleService';
import { loadTheme, saveTheme, type ThemeMode } from '../../services/themeService';

/**
 * Account settings — available to every signed-in role.
 *
 * This is the ONLY tab a normal user sees. Kept deliberately small for the MVP
 * but isolated as its own component so profile/notification/preference sections
 * can be added without touching the admin tabs.
 */
export default function AccountSettingsTab({
  currentUser,
  onProfileUpdated,
}: {
  currentUser: User;
  onProfileUpdated: () => void;
}) {
  const [name, setName] = useState(currentUser.name);
  /** Seeded from storage (or the OS) so the buttons show the live state. */
  const [theme, setTheme] = useState<ThemeMode>(() => loadTheme());
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Uses the same access-boundary selector as the home page, so this summary can
  // never show a module the user isn't entitled to.
  const myModules = useMemo<AppModule[]>(
    () => moduleService.listModulesForUser(currentUser),
    [currentUser],
  );

  /* Which group(s) this person is in, and what each one shares with them —
     "My modules" above already includes anything granted through a group
     (the server resolves that before GET /modules ever replies), but it
     reads as one flat list with no hint that some of it exists BECAUSE of a
     group membership rather than a direct share. This answers that
     specifically: not just "what can I open" but "why can I open it". */
  const [myGroups, setMyGroups] = useState<MyGroup[] | null>(null);
  const [groupsError, setGroupsError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    groupService
      .mine()
      .then((groups) => {
        if (!cancelled) setMyGroups(groups);
      })
      .catch((error) => {
        if (!cancelled) setGroupsError(error instanceof Error ? error.message : 'Could not load your groups.');
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser.id]);

  const saveProfile = async () => {
    setError(null);
    setMessage(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (password && password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSaving(true);
    try {
      // NOTE: a user may only ever update their OWN record here. Role and module
      // assignment are intentionally not editable from this tab — a real backend
      // must also ignore any role/assignment fields sent from this endpoint.
      await userService.updateUser(currentUser.id, {
        name,
        email: currentUser.email,
        role: currentUser.role,
        ...(password ? { password } : {}),
      });
      setPassword('');
      setConfirmPassword('');
      setMessage('Profile updated.');
      onProfileUpdated();
    } catch (err: any) {
      setError(err?.message || 'Could not update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Appearance is a per-browser display preference, so it lives with the
          account rather than in the portal's saved data. */}
      <section>
        <h4 className="text-sm font-semibold text-[#0F2942]">Appearance</h4>
        <p className="mt-1 text-[11px] text-slate-500">
          Applies to the whole application, including the app builder and dashboards.
        </p>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {([
            { id: 'light' as ThemeMode, label: 'Light', Icon: Sun },
            { id: 'dark' as ThemeMode, label: 'Dark', Icon: Moon },
          ]).map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              aria-pressed={theme === id}
              onClick={() => {
                setTheme(id);
                saveTheme(id);
              }}
              className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                theme === id
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>
      </section>

      <section>
        <h4 className="text-sm font-semibold text-[#0F2942]">Profile</h4>
        <div className="mt-3 space-y-3">
          <label className="block">
            <span className="text-xs font-semibold text-slate-600">Name</span>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
            />
          </label>

          <label className="block">
            <span className="text-xs font-semibold text-slate-600">Email</span>
            <input
              value={currentUser.email}
              readOnly
              className="mt-1 w-full cursor-not-allowed rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-500"
            />
            <span className="mt-1 block text-[11px] text-slate-400">
              Contact an administrator to change your email.
            </span>
          </label>

          <div>
            <span className="text-xs font-semibold text-slate-600">Role</span>
            <p className="mt-1 text-sm text-slate-700">
              {/* The real role name — a custom role (e.g. "Editor") used
                  to always show as plain "User" here. */}
              {roleService.myRoleName() ?? currentUser.role}
            </p>
          </div>
        </div>
      </section>

      <section className="border-t border-slate-100 pt-5">
        <h4 className="text-sm font-semibold text-[#0F2942]">Change password</h4>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="text-xs font-semibold text-slate-600">New password</span>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Leave blank to keep current"
              className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-teal-400"
            />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-slate-600">Confirm password</span>
            <input
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-teal-400"
            />
          </label>
        </div>
      </section>

      <section className="border-t border-slate-100 pt-5">
        <h4 className="text-sm font-semibold text-[#0F2942]">My modules</h4>
        {myModules.length === 0 ? (
          <p className="mt-2 rounded-lg border border-dashed border-slate-300 px-3 py-5 text-center text-xs text-slate-400">
            No modules yet. Use “Add Module” on the home page to create one.
          </p>
        ) : (
          <ul className="mt-2 space-y-1.5">
            {myModules.map((module) => (
              <li
                key={module.id}
                className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2"
              >
                <span className="truncate text-xs font-medium text-slate-700">
                  {module.title || module.name}
                </span>
                <span className="ml-2 flex-shrink-0 text-[10px] uppercase tracking-wide text-slate-400">
                  {module.ownerId === currentUser.id ? 'Owned' : 'Assigned'}
                </span>
              </li>
            ))}
          </ul>
        )}
        {/* Sharing is intentionally NOT offered here. A normal user can never share
            a module — neither one they own nor one assigned to them. See
            ROLE_PERMISSIONS: 'modules:share' is admin-only. */}
      </section>

      <section className="border-t border-slate-100 pt-5">
        <h4 className="flex items-center gap-1.5 text-sm font-semibold text-[#0F2942]">
          <UsersIcon className="h-4 w-4 text-slate-400" />
          My groups
        </h4>
        <p className="mt-1 text-[11px] text-slate-500">
          A group's applications land in "My modules" above too — this is where they come from.
        </p>

        {groupsError ? (
          <p role="alert" className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-600">
            {groupsError}
          </p>
        ) : myGroups === null ? (
          <p className="mt-2 text-xs text-slate-400">Loading…</p>
        ) : myGroups.length === 0 ? (
          <p className="mt-2 rounded-lg border border-dashed border-slate-300 px-3 py-5 text-center text-xs text-slate-400">
            You are not in any group yet. An administrator adds you to one to share several
            applications at once.
          </p>
        ) : (
          <ul className="mt-2 space-y-2">
            {myGroups.map((group) => {
              const sharedModules = group.moduleIds
                .map((id) => moduleService.getModuleById(id))
                .filter((module): module is AppModule => module !== null);
              return (
                <li key={group.id} className="rounded-lg border border-slate-200 px-3 py-2.5">
                  <span className="block truncate text-xs font-semibold text-slate-700">
                    {group.name}
                  </span>
                  {sharedModules.length === 0 ? (
                    <span className="mt-1 block text-[11px] text-slate-400">
                      No applications shared with this group yet.
                    </span>
                  ) : (
                    <ul className="mt-1.5 space-y-1">
                      {sharedModules.map((module) => (
                        <li
                          key={module.id}
                          className="truncate text-[11.5px] text-slate-600 before:mr-1.5 before:text-slate-300 before:content-['•']"
                        >
                          {module.title || module.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {error && (
        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-600">
          {error}
        </p>
      )}
      {message && (
        <p className="rounded-lg bg-teal-50 px-3 py-2 text-xs font-medium text-teal-700">{message}</p>
      )}

      <div className="border-t border-slate-100 pt-4">
        <button
          type="button"
          onClick={() => void saveProfile()}
          disabled={saving}
          className="rounded-lg bg-[#0F2942] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#16395C] disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}
